import { Icon } from "@mdi/react";
import * as Sentry from "@sentry/react";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import AllInboxIcon from "@material-ui/icons/AllInbox";
import { mdiChevronDown } from "@mdi/js";
import { Button, Popover, Tooltip } from "antd";
import { widgetHoverAtom } from "atoms/interaction";
import classnames from "classnames";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracActivity";
import { DEFAULT_RIG_FRAC_RECENT_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracRecentActivity";
import { useAtomValue } from "jotai";
import omit from "lodash/omit";
import { updateChart } from "store/features/charts/chartSlice";
import styled from "styled-components/macro";
import { ChartRequestType, RigFracActivityChartRequestType } from "types";

import { useChartEntities } from "hooks/charts";

import { visibilityToggleStyles } from "../../shared/SharedStyles";
import ChartBinsSettings from "../components/ChartBinsSettings";

// The left y-axis title the button sits beside, read from the drawn options so
// the two always agree. Activity Over Time keeps an array of y-axes.
const leftAxisTitle = (options: string): string => {
  try {
    const yAxis = JSON.parse(options || "{}")?.yAxis;
    const name = (Array.isArray(yAxis) ? yAxis[0] : yAxis)?.name;
    return typeof name === "string" ? name : "";
  } catch (error) {
    // Malformed persisted options are a corruption signal, as computeBottomOffsetPx reports.
    Sentry.withScope((scope) => {
      scope.setContext("leftAxisTitle", { options });
      scope.setLevel("warning");
      Sentry.captureException(error);
    });
    return "";
  }
};

/**
 * The chart's own bin override, beside the left y-axis title as on the wells
 * bar charts. Off, the chart follows the main focus bins; on, it keeps its top
 * Max Bins groups by the chart's own value and folds the rest into Others, or
 * shows every group when Max Bins is empty. Saved on the chart's request.
 */
function RigFracBinSettingsToggle() {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { request, chartType, options, screenshot, dataTable } = useChartEntities(id);
  const isHover = useAtomValue(widgetHoverAtom)?.[id] ?? false;
  const [open, setOpen] = useState(false);

  const selections = request as unknown as Maybe<
    Partial<RigFracActivityChartRequestType>
  >;
  const maxBins = selections?.maxBins;
  const isActive = maxBins !== undefined;
  const isRecent = chartType === EvaChart.RigFracRecentActivity;
  const title = useMemo(() => leftAxisTitle(options), [options]);

  const setMaxBins = (next: Nullable<number> | undefined) => {
    dispatch(
      updateChart({
        id,
        request: {
          // Seeded per chart type, so the other type's fields never reach a saved widget.
          ...(isRecent
            ? DEFAULT_RIG_FRAC_RECENT_ACTIVITY_REQUEST
            : DEFAULT_RIG_FRAC_ACTIVITY_REQUEST),
          ...omit(selections, ["maxBins"]),
          ...(next !== undefined ? { maxBins: next } : {})
        } as unknown as ChartRequestType
      })
    );
  };

  const content = (
    <ChartBinsSettings
      active={isActive}
      maxBins={maxBins}
      onChange={(active, next) => setMaxBins(active ? next : undefined)}
      testIdPrefix="rig-frac"
    />
  );

  const visible = (isHover || open) && !screenshot?.active && !dataTable?.active;

  return (
    <Wrapper
      className={classnames(isRecent ? "recent" : "activity", { visible })}
      data-testid="rig-frac-bin-settings-anchor">
      <TitleSpacer aria-hidden>
        {title}
        {!isRecent && <Icon path={mdiChevronDown} size={0.75} />}
      </TitleSpacer>
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="left"
        content={content}>
        <Tooltip title="Bin Settings">
          <BinButton
            data-testid="rig-frac-bin-settings-trigger"
            aria-label="Bin Settings"
            aria-pressed={isActive}
            size="small"
            $active={isActive}
            icon={<AllInboxIcon />}
          />
        </Tooltip>
      </Popover>
    </Wrapper>
  );
}

export default RigFracBinSettingsToggle;

// Rotated and centred on the left y-axis title exactly as RigFracMetricSelector's
// left button is, so the bin button lands just past the title's end.
const Wrapper = styled.div`
  ${visibilityToggleStyles};
  position: absolute;
  z-index: 1;
  left: 0;
  transform-origin: center;

  /* Only the button takes the pointer, never the title it sits beside. */
  &.visible {
    pointer-events: none;
  }

  &.activity {
    top: calc(50% - 27px);
    transform: translate(calc(-50% + 17px), -50%) rotate(-90deg);
  }

  /* Recent Activity: the category title sits 16px in (grid.left - nameGap) and
     centres on a grid inset 76px top, 52px bottom. */
  &.recent {
    top: calc(50% + 12px);
    transform: translate(calc(-50% + 16px), -50%) rotate(-90deg);
  }
`;

// Takes the title's footprint (same metrics as the metric button) without showing.
const TitleSpacer = styled.span`
  display: inline-block;
  visibility: hidden;
  padding: 2px 12px;
  border: 1px solid transparent;
  font-size: 15px;
  font-weight: var(--fontWeightMedium);
  white-space: nowrap;

  /* Activity's metric button carries a chevron; Recent's left title has none. */
  .activity > & {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding-right: 6px;
  }
`;

const BinButton = styled(Button)<{ $active: boolean }>`
  position: absolute;
  top: 50%;
  left: calc(100% + 4px);
  transform: translateY(-50%) rotate(90deg);
  pointer-events: auto;
  display: grid;
  justify-content: center;
  align-items: center;
  color: ${(p) => (p.$active ? "var(--color-primary)" : "var(--color-text)")};
  border-color: ${(p) => (p.$active ? "var(--color-primary)" : undefined)};
`;
